import React, { MouseEventHandler, useState } from "react";
import { Destination, DestinationType, Organization, ProviderType, SourceWithConnections } from "../common/ObjectTypes";
import { SortedDown, SortedUp, SortingArrows, Clear } from "../common/ProjectIcons";
import NoResults from "../common/NoResults";
import Pagination from "../common/Pagination";
import { page_size } from "../../api/apiUtils";
import { useTranslation } from "react-i18next";
import { useTimezone } from "../../context/TimezoneContext";
import { formatSourceDatetime } from "./SourceHelperFunctions";
import { tableClasses, textClasses, containerClasses, paginationClasses, filterClasses } from "../common/tailwindClassnames";
import GlobalSearch from "../common/GlobalSearch";
import SelectInput from "../common/SelectInput";

type SourcesListProps = {
    sources: SourceWithConnections[],
    totalCount: number,
    take: number,
    offset: number,
    onPageClicked: MouseEventHandler,
    loading: boolean,
    organizations: Organization[],
    loadingOrganizations: boolean,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    destinationUrls: string[],
    loadingDestinationUrls: boolean,
    providerTypes: ProviderType[],
    loadingProviderTypes: boolean,
    globalSearchStr: string,
    onChangeGlobalSearch: Function,
    onChangeOwner: Function,
    ownerFilterValue: any,
    onChangeEndpoint: Function,
    endpointFilterValue: any,
    onChangeProviderType: Function,
    providerTypeFilterValue: any,
    onChangeDestinationType: Function,
    destinationTypeFilterValue: any,
    requestSort: Function,
    onViewDetails: Function,
    onViewConnectionDetails: Function,
}

const SourcesList = ({
    sources,
    totalCount,
    take,
    offset,
    onPageClicked,
    loading,
    organizations,
    loadingOrganizations,
    destinationTypes,
    loadingDestinationTypes,
    destinationUrls,
    loadingDestinationUrls,
    providerTypes,
    loadingProviderTypes,
    globalSearchStr,
    onChangeGlobalSearch,
    onChangeOwner,
    ownerFilterValue,
    onChangeEndpoint,
    endpointFilterValue,
    onChangeProviderType,
    providerTypeFilterValue,
    onChangeDestinationType,
    destinationTypeFilterValue,
    requestSort,
    onViewDetails,
    onViewConnectionDetails,
}: SourcesListProps) => {
    const { t } = useTranslation();
    const { timezone } = useTimezone();
    const [sortDirection, setSortDirection] = useState(true);
    const [sortByColumn, setSortByColumn] = useState("external_id");

    return (
        <div className={containerClasses.page}>
            <div className="mb-12 flex justify-between">
                <div>
                    <h1 data-testid="sources_page_header" className={textClasses.heading}>
                        {t("Sources")}
                    </h1>
                </div>
            </div>
            {/* Horizontal filter bar */}
            <div className="flex flex-row gap-3 mb-3 flex-wrap">
                {/* Search */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black dark:text-white">{t("Search")}</span>
                    <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} savedString={globalSearchStr} />
                </div>
                {/* Destination */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black dark:text-white">{t("Destination")}</span>
                    <SelectInput
                        name="destination_url__in"
                        options={destinationUrls.map(url => {
                            let hostname = url;
                            try { hostname = new URL(url).hostname; } catch {}
                            return { label: hostname, value: url };
                        })}
                        value={endpointFilterValue}
                        onChange={onChangeEndpoint}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black dark:text-white">{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Provider Type */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black dark:text-white">{t("Provider Type")}</span>
                    <SelectInput
                        name="provider_type__in"
                        options={providerTypes.map(p => ({ label: p.name, value: p.value }))}
                        value={providerTypeFilterValue}
                        onChange={onChangeProviderType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingProviderTypes}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Destination Type */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black dark:text-white">{t("Destination Type")}</span>
                    <SelectInput
                        name="destination_type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.value }))}
                        value={destinationTypeFilterValue}
                        onChange={onChangeDestinationType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
            </div>
            {/* Filter chips */}
            {(globalSearchStr || endpointFilterValue || ownerFilterValue || providerTypeFilterValue || destinationTypeFilterValue) &&
                <div className="flex flex-row flex-wrap gap-2 mb-3">
                    {globalSearchStr && <span className={filterClasses.tag}>
                        {globalSearchStr}
                        <span className="cursor-pointer text-dark-green dark:text-dark-green-100" onClick={() => onChangeGlobalSearch('')}><Clear /></span>
                    </span>}
                    {endpointFilterValue && <span className={filterClasses.tag}>
                        {(endpointFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green dark:text-dark-green-100" onClick={() => onChangeEndpoint(null)}><Clear /></span>
                    </span>}
                    {ownerFilterValue && <span className={filterClasses.tag}>
                        {(ownerFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green dark:text-dark-green-100" onClick={() => onChangeOwner(null)}><Clear /></span>
                    </span>}
                    {providerTypeFilterValue && <span className={filterClasses.tag}>
                        {(providerTypeFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green dark:text-dark-green-100" onClick={() => onChangeProviderType(null)}><Clear /></span>
                    </span>}
                    {destinationTypeFilterValue && <span className={filterClasses.tag}>
                        {(destinationTypeFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green dark:text-dark-green-100" onClick={() => onChangeDestinationType(null)}><Clear /></span>
                    </span>}
                </div>
            }
            {/* Table */}
            <div className="flex flex-col w-full">
                <table className={tableClasses.container}>
                    <thead className={tableClasses.header}>
                        <tr>
                            <th className={tableClasses.headerCell} style={{ maxWidth: "300px" }}>
                                {t("External ID")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("external_id", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("external_id");
                                }}>
                                    {sortByColumn === "external_id" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Connection")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("integration__id", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("integration__id");
                                }}>
                                    {sortByColumn === "integration__id" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Destinations")}
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Created At")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("created_at", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("created_at")
                                }}>
                                    {sortByColumn === "created_at" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    {loading ?
                        <tbody className="text-sm">
                            {[...Array(8)].map((_, i) => (
                                <tr key={i} className={tableClasses.row} data-testid="loading">
                                    <td className={tableClasses.cell}><div className="h-4 w-32 bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse" /></td>
                                    <td className={tableClasses.cell}><div className="h-4 w-40 bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse" /></td>
                                    <td className={tableClasses.cell}><div className="h-4 w-32 bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse" /></td>
                                    <td className={tableClasses.cell}><div className="h-4 w-24 bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse" /></td>
                                </tr>
                            ))}
                        </tbody>
                            : <tbody className="text-sm">
                                {sources.map((src: any) => {
                                    return (
                                        <tr key={src.source.id}
                                            className={tableClasses.row}>
                                            <td className={tableClasses.cell} style={{ maxWidth: "300px" }}>
                                                <span className="block truncate" title={src.source.external_id}>
                                                    {src.source.external_id}
                                                </span>
                                            </td>
                                            <td className={tableClasses.cell}>
                                                <span
                                                    className="text-blue-basic dark:text-dark-blue-300 transition-colors duration-300 cursor-pointer hover:underline"
                                                    onClick={() => onViewConnectionDetails(src.source.provider.id)}
                                                >
                                                    {src.source.provider.name}
                                                </span>
                                            </td>
                                            <td className={tableClasses.cell}>
                                                {src.source.destinations.map((dest: Destination) => {
                                                    let dest_url: string | URL;
                                                    try {
                                                        dest_url = new URL(dest.base_url).hostname
                                                    } catch (err) {
                                                        dest_url = ""
                                                    }
                                                    return (<p className="text-blue-basic dark:text-dark-blue-300 transition-colors duration-300 cursor-pointer" key={dest.id} onClick={() => onViewDetails(dest.id)}>{dest_url}</p>)
                                                })}
                                            </td>
                                            <td className={tableClasses.cell}>
                                                {formatSourceDatetime(src.source.created_at, timezone)}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>}
                        {!loading && sources.length === 0 &&
                            <tbody><tr><td colSpan={4} className="py-16">
                                <NoResults
                                    headerMsg={t("No Results Found")}
                                    labelMsg={`${t("You may need to adjust your search or filters to find what you are looking for")}.`}
                                />
                            </td></tr></tbody>}
                </table>
                {totalCount > page_size && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    initialPage={Math.floor(offset / take)}
                    totalCount={totalCount}
                    take={take}
                    loading={loading}
                    mainclassname={paginationClasses.container}
                />}
            </div>
        </div>
    )
};

export default SourcesList;
